import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Box, Chip, Stack } from '@mui/material';
import { GridColDef, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { isArray } from 'lodash-es';
import { useDebouncedCallback } from 'use-debounce';

import { sortListAlphabetical } from '../../../Draft/Component/Helper';
import { IObject } from '../../../interfaces/IObject';
import OverflowTip from '../../../RiverusUI/Components/OverflowTip';
import DataGridTable from '../../../RiverusUI/DataGrid/DataGridTable';
import { GridCellExpand } from '../../../RiverusUI/DataGrid/GridCellExpand';
import NameAvatar from '../../../RiverusUI/DataGrid/NameAvatar';
import TableChipList from '../../../RiverusUI/DataGrid/TableChipList';
import TableHeaderWithSearch from '../../../RiverusUI/DataGrid/TableHeaderWIthSearch';
import TableSearchInputComponent from '../../../RiverusUI/DataGrid/TableSearchInputComponent';
import TableSearchSelectInput from '../../../RiverusUI/DataGrid/TableSearchSelectInput';
import {
  fetchAllContractCategory,
  fetchGroups,
  getUsers,
} from '../../../Services/Approval';
import {
  fetchTemplateTags,
  getContractTypes,
} from '../../../Services/Template';
import { selectTypeDocument } from '../StaticData';

interface Props {
  setSelectedRow: Dispatch<SetStateAction<GridRowId[]>>;
  data: any;
  isLoading: boolean;
  pageNumber: number;
  setPageNumberChange: Dispatch<SetStateAction<number>>;
  setFilters: Dispatch<SetStateAction<IObject>>;
  setSorting?: Dispatch<SetStateAction<GridSortModel>>;
  filters?: IObject;
  selectedRow?: GridRowId[];
  checkBoxSelection?: boolean;
  page: string;
  activeTab: string;
  refreshKey?: boolean;
}

const TemplateTable = (props: Props) => {
  const {
    setSelectedRow,
    data,
    isLoading,
    pageNumber,
    setPageNumberChange,
    setFilters,
    setSorting,
    filters,
    selectedRow,
    checkBoxSelection = false,
    page,
    activeTab,
    refreshKey,
  } = props;

  const [searchDraft, setSearchDraft] = useState<boolean>(false);
  const [searchGroups, setSearchGroups] = useState<boolean>(false);
  const [searchTags, setSearchTags] = useState<boolean>(false);
  const [searchAssignee, setSearchAssignee] = useState<boolean>(false);
  const [searchContractType, setSearchContractType] = useState<boolean>(false);
  const [searchCategory, setSearchCategory] = useState<boolean>(false);
  const [searchSupportType, setSearchSupportType] = useState<boolean>(false);

  const { data: tags } = useQuery({
    queryKey: ['template-tags'],
    queryFn: fetchTemplateTags,
    select: (response: any) => {
      return response.results.map((data: any) => {
        const newData = {
          id: data.id,
          name: data.tag_name,
        };
        return newData;
      });
    },
  });

  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
  });

  const { data: contracts } = useQuery({
    queryKey: ['template-contracts'],
    queryFn: getContractTypes,
    select: (response: any) => response.results,
  });

  const { data: contractCategory } = useQuery({
    queryKey: ['draft_contract_Category'],
    queryFn: fetchAllContractCategory,
    select: (response: any) => response.results || [],
  });

  const { data: authorData } = useQuery({
    queryKey: ['author'],
    queryFn: async () => {
      const response = await getUsers();
      const users = response?.users || [];
      for (let i = 0; i < users.length; i++) {
        const name = users[i].first_name + ' ' + users[i].last_name;
        users[i].name = name;
      }
      return users;
    },
  });

  const handleFilterChange = useDebouncedCallback(
    (label: string, value: string | string[]) => {
      if (value?.length) {
        setPageNumberChange(0);
      }
      setFilters((prev: IObject) => ({ ...prev, [label]: value }));
    },
    1000
  );

  const handleGroupClick = React.useCallback(
    (groups: any) => {
      handleFilterChange('group', groups);
      setSearchGroups(true);
    },
    [handleFilterChange]
  );

  const handleTagClick = React.useCallback(
    (tags: any) => {
      handleFilterChange('tags', tags);
      setSearchTags(true);
    },
    [handleFilterChange]
  );

  useEffect(() => {
    if (!filters?.name) {
      setSearchDraft(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!filters?.contract_type) {
      setSearchContractType(false);
    }
    if (!filters?.contract_category) {
      setSearchCategory(false);
    }
    if (!filters?.support_document_type) {
      setSearchSupportType(false);
    }
    if (!filters?.groups) {
      setSearchGroups(false);
    }
    if (!filters?.Tags) {
      setSearchTags(false);
    }
    if (!filters?.created_by) {
      setSearchAssignee(false);
    }
  }, [refreshKey]);

  const handleContractTypeClick = React.useCallback(
    (contract_type: any) => {
      setFilters((prev: IObject) => ({
        ...prev,
        contract_type: [contract_type?.id],
      }));
      if (contract_type?.id) {
        setPageNumberChange(0);
      }
      setSearchContractType(true);
    },
    [handleFilterChange]
  );

  const handleCategoryClick = React.useCallback(
    (category: any) => {
      setFilters((prev: IObject) => ({
        ...prev,
        contract_category: [category?.id],
      }));
      if (category?.id) {
        setPageNumberChange(0);
      }
      setSearchCategory(true);
    },
    [handleFilterChange]
  );

  const filteredTags = useMemo(() => {
    const uniqueNames = new Set();
    return tags?.filter((tag: any) => {
      if (!uniqueNames.has(tag?.name)) {
        uniqueNames.add(tag?.name);
        return true;
      }
      return false;
    });
  }, [tags]);

  const columns = React.useMemo<GridColDef<any>[]>(() => {
    let cols: GridColDef<any>[] = [
      {
        field: 'name',
        headerName: 'Title',
        minWidth: 250,
        flex: 1,
        sortable: !searchDraft,
        renderHeader: () => {
          return searchDraft ? (
            <TableSearchInputComponent
              key="name"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, name: null }));
                setSearchDraft(false);
              }}
              placeholder="Search Title"
              handleChange={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
                if (e.target.value?.length) {
                  handleFilterChange('name', e.target.value);
                }
              }}
            />
          ) : (
            <Stack onClick={(e) => e.stopPropagation()}>
              <TableHeaderWithSearch
                title="Template Name"
                setIsSearch={setSearchDraft}
              />
            </Stack>
          );
        },
        renderCell: (Params: any) => {
          return (
            <GridCellExpand
              value={Params?.row?.name || Params?.row?.file_name}
              width={Params.colDef.computedWidth}
              onClick={() => {
                window.open(`/template/${Params.row.id}/${page}`);
              }}
              cellStyle={{
                cursor: 'pointer',
              }}
            />
          );
        },
      },
      {
        field: 'support_document_type',
        headerName: 'Support Document Type',
        minWidth: 260,
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params?.row?.support_document_type && (
            <Chip
              sx={{
                background: '#CDE7BD',
                color: 'riSecondary.900',
              }}
              label={<OverflowTip value={params?.row?.support_document_type} />}
              onClick={() =>
                handleFilterChange('support_document_type', [
                  params?.row?.support_document_type,
                ])
              }
            />
          ),
        renderHeader: () => {
          return searchSupportType ? (
            <TableSearchInputComponent
              key="support_document_type"
              setIsSearch={() => {
                setFilters((prev) => ({
                  ...prev,
                  support_document_type: null,
                }));
                setSearchSupportType(false);
              }}
              placeholder="Search support document type"
              selectedValue={
                filters?.support_document_type
                  ? filters.support_document_type
                  : []
              }
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.support_document_type)
                      ? (filters?.support_document_type?.[0] as string)
                      : 'support_document_type'
                  }
                  label="Select support document type"
                  options={sortListAlphabetical(selectTypeDocument)}
                  labelKey="name"
                  valueKey="name"
                  value={
                    filters?.support_document_type
                      ? filters.support_document_type
                      : []
                  }
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('support_document_type', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        support_document_type: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Support Document Type"
              width="180px"
              setIsSearch={setSearchSupportType}
            />
          );
        },
      },
      {
        field: 'contract_type',
        headerName: 'Contract Type',
        maxWidth: 300,
        width: 250,
        sortable: false,
        renderHeader: () => {
          return searchContractType ? (
            <TableSearchInputComponent
              key="contract_type"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, contract_type: null }));
                setSearchContractType(false);
              }}
              placeholder="Search Contract Type"
              selectedValue={
                filters?.contract_type ? filters.contract_type : []
              }
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.contract_type)
                      ? (filters?.contract_type?.[0] as string)
                      : 'contract_type'
                  }
                  label="Select contract type"
                  labelKey="displayName"
                  options={sortListAlphabetical(contracts, 'displayName')}
                  valueKey="id"
                  value={filters?.contract_type ? filters.contract_type : []}
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('contract_type', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        contract_type: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Contract Type"
              setIsSearch={setSearchContractType}
            />
          );
        },
        renderCell: (params) => (
          <Chip
            sx={{
              background: '#C4DBFF',
              color: 'riSecondary.900',
            }}
            label={
              <OverflowTip value={params?.row?.contract_type?.displayName} />
            }
            onClick={() => handleContractTypeClick(params?.row?.contract_type)}
          />
        ),
      },
      {
        field: 'contract_category',
        headerName: 'Contract Category',
        minWidth: 260,
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params?.row?.contract_category?.name && (
            <Chip
              sx={{
                background: '#C4DBFF',
                color: 'riSecondary.900',
              }}
              label={
                <OverflowTip value={params?.row?.contract_category?.name} />
              }
              onClick={() =>
                handleCategoryClick(params?.row?.contract_category)
              }
            />
          ),
        renderHeader: () => {
          return searchCategory ? (
            <TableSearchInputComponent
              key="contract_category"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, contract_category: null }));
                setSearchCategory(false);
              }}
              placeholder="Search contract category"
              selectedValue={
                filters?.contract_category ? filters.contract_category : []
              }
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.contract_category)
                      ? (filters?.contract_category?.[0] as string)
                      : 'contract_category'
                  }
                  label="Select contract category"
                  options={sortListAlphabetical(contractCategory)}
                  labelKey="name"
                  valueKey="id"
                  value={
                    filters?.contract_category ? filters.contract_category : []
                  }
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('contract_category', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        contract_category: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Contract Category"
              width="140px"
              setIsSearch={setSearchCategory}
            />
          );
        },
      },
      {
        field: 'groups',
        headerName: 'Groups',
        maxWidth: 300,
        width: 260,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => {
          const selectedFilters: any = filters?.group || [];
          let reOrderGroups = params?.row?.group || [];
          if (selectedFilters?.length) {
            const filterSelectedGroups = reOrderGroups?.filter((item: any) =>
              selectedFilters?.includes(item?.name)
            );
            const filterNotSelectedGroups = reOrderGroups?.filter(
              (item: any) => !selectedFilters?.includes(item?.name)
            );
            reOrderGroups = [
              ...filterSelectedGroups,
              ...filterNotSelectedGroups,
            ];
          }
          return (
            <TableChipList
              id={`group-${params?.row?.id}`}
              chipColor="#DCBDE7"
              list={reOrderGroups}
              onClick={handleGroupClick}
            />
          );
        },
        renderHeader: () => {
          return searchGroups ? (
            <TableSearchInputComponent
              key="group"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, group: null }));
                setSearchGroups(false);
              }}
              placeholder="Search Groups"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.group)
                      ? (filters?.group?.[0] as string)
                      : 'group'
                  }
                  options={sortListAlphabetical(groups)}
                  label="Select Groups"
                  valueKey="name"
                  value={filters?.group ? filters.group : []}
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('group', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        group: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Groups"
              setIsSearch={setSearchGroups}
            />
          );
        },
      },
      {
        field: 'tags',
        headerName: 'Tags',
        minWidth: 260,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const selectedFilters: any = filters?.tags || [];
          let reOrderTags = params?.row?.tags || [];
          if (selectedFilters?.length) {
            const filterSelectedTags = reOrderTags?.filter((item: any) =>
              selectedFilters?.includes(item?.tag_name)
            );
            const filterNotSelectedTags = reOrderTags?.filter(
              (item: any) => !selectedFilters?.includes(item?.tag_name)
            );
            reOrderTags = [...filterSelectedTags, ...filterNotSelectedTags];
          }
          return (
            <TableChipList
              id={`tags-${params?.row?.id}`}
              labelKey="tag_name"
              valueKey="tag_name"
              chipColor="#DCBDE7"
              list={reOrderTags}
              onClick={handleTagClick}
            />
          );
        },
        renderHeader: () => {
          return searchTags ? (
            <TableSearchInputComponent
              key="tags"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, tags: null }));
                setSearchTags(false);
              }}
              placeholder="Search Tags"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.tags)
                      ? (filters?.tags?.[0] as string)
                      : 'tags'
                  }
                  options={sortListAlphabetical(filteredTags)}
                  valueKey="name"
                  label="Select Tags"
                  value={filters?.tags ? filters.tags : []}
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('tags', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        tags: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch title="Tags" setIsSearch={setSearchTags} />
          );
        },
      },
      {
        field: 'created_by',
        headerName: 'Uploaded By',
        maxWidth: 260,
        width: 220,
        sortable: false,
        minWidth: 260,
        renderHeader: () => {
          return searchAssignee ? (
            <TableSearchInputComponent
              key="created_by"
              setIsSearch={() => {
                setFilters((prev) => ({ ...prev, created_by: null }));
                setSearchAssignee(false);
              }}
              placeholder="Search uploader"
              renderCustomInput={() => (
                <TableSearchSelectInput
                  key={
                    isArray(filters?.created_by)
                      ? (filters?.created_by?.[0] as string)
                      : 'created_by'
                  }
                  options={sortListAlphabetical(authorData)}
                  valueKey="id"
                  label="Select user"
                  value={filters?.created_by ? filters.created_by : []}
                  onChange={(value: string[]) => {
                    if (value.length > 0) {
                      handleFilterChange('created_by', value);
                    } else {
                      setFilters((prev: IObject) => ({
                        ...prev,
                        created_by: null,
                      }));
                    }
                  }}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <TableHeaderWithSearch
              title="Uploaded By"
              setIsSearch={setSearchAssignee}
            />
          );
        },
        renderCell: (Params) => {
          return (
            <NameAvatar
              firstName={Params?.row?.created_by?.first_name}
              lastName={Params?.row?.created_by?.last_name}
            />
          );
        },
      },
      {
        field: 'draft_count',
        headerName: 'Drafts',
        minWidth: 120,
        flex: 1,
        sortable: true,
        renderCell: (Params) => <Box>{Params?.row?.used_in_drafts || 0}</Box>,
      },
    ];

    if (activeTab === 'template') {
      cols = cols.filter(
        (col) =>
          col.field !== 'support_document_type' &&
          col.field !== 'contract_category'
      );
    }

    return cols;
  }, [
    searchDraft,
    setFilters,
    handleFilterChange,
    page,
    handleGroupClick,
    searchGroups,
    filters,
    groups,
    handleTagClick,
    handleContractTypeClick,
    searchTags,
    searchCategory,
    handleCategoryClick,
    tags,
    searchAssignee,
    authorData,
    activeTab,
    searchSupportType,
    searchContractType,
    isLoading,
  ]);

  return (
    <DataGridTable
      rows={data?.results || []}
      columns={columns}
      rowCount={data?.count}
      isLoading={isLoading}
      setSelectedRow={setSelectedRow}
      pageNumber={pageNumber}
      setPageNumberChange={setPageNumberChange}
      setSorting={setSorting}
      checkboxSelection={checkBoxSelection}
      selectedRow={selectedRow}
      columnVisibilityModel={{
        groups: activeTab === 'template' ? true : false,
      }}
    />
  );
};

export default TemplateTable;
